import React, { useMemo, useRef, useState, useEffect } from "react";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Link, useNavigate } from "react-router-dom";
import { SvgIcon } from "@/core/components/svgIcon/SvgIcon";
import { imageList } from "@/core/utils/imageList";
import { createRoute, ROUTES } from "@/core/constants/app";
import { slugify } from "@/core/utils/stringUtils";
import { apiService } from "@/core/api";
import { API_ENDPOINTS } from "@/core/constants/endpoints";
import "@features/calls/pages/CallsPage.scss";
import {
  FundingGoal,
  FundingGoalEntityType,
  MatchingCallsFilters,
} from "../model/types";
import { useMatchingCallsViewModel } from "../viewmodel/useMatchingCallsViewModel";
import Loader from "@/core/components/loader/Loader";
import "./MatchingCalls.scss";
import { useUnit } from "effector-react";
import {
  analyzeCallRequested,
  $goalsStore,
  fetchGoalsRequested,
  getEntityCallRequested,
  setMatchingCallsSort,
} from "../store";
import {
  FiltersDrawer,
  FilterState,
} from "@features/calls/components/FiltersDrawer";
import { CreateGoalModal } from "./CreateGoalModal";
import { goalsService } from "../service";
import { fetchSingleGoalRequested } from "../store";
import { Tooltip } from "@/core/components/tooltip/Tooltip";
import { Tags } from "@/core/components/tags/Tags";
import { callsService } from "@/features/calls/service/api";
import { useToast } from "@/core/hooks/useToast";
import { $userBasicInfo } from "@/features/user/store";

export interface MatchingCallsProps {
  goal: FundingGoal;
  onBack: () => void;
}

type SortOption = "score_desc" | "created_desc" | "deadline_asc";

export const MatchingCalls: React.FC<MatchingCallsProps> = ({
  goal,
  onBack,
}) => {
  const {
    matchingCalls: callsFromStore,
    loading,
    entity,
  } = useUnit($goalsStore);
  const [calls, setCalls] = useState(callsFromStore);

  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFiltersDrawerOpen, setIsFiltersDrawerOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const setSortInStore = useUnit(setMatchingCallsSort);
  const [expandedSectors, setExpandedSectors] = useState<Set<string>>(
    new Set(),
  );
  const loadMoreButtonRef = useRef<HTMLButtonElement>(null);
  const [filters, setFilters] = useState<MatchingCallsFilters>({
    sort: "created",
  });
  const analyzeCall = useUnit(analyzeCallRequested);
  const getEntityCall = useUnit(getEntityCallRequested);
  const fetchGoal = useUnit(fetchSingleGoalRequested);
  const {
    hasMoreCalls,
    loadMoreCalls,
    loadMore,
    fetchMatchingCallsWithFilters,
    goalsState,
  } = useMatchingCallsViewModel(goal);
  const sortBy = goalsState.matchingCallsSort as SortOption;
  const fetchGoals = useUnit(fetchGoalsRequested);
  const toast = useToast();
  const [userBasicInfo] = useUnit([$userBasicInfo]);
  const { pro_user } = userBasicInfo || {};

  const getScoreColor = (score: number) => {
    if (score >= 67) return "#067647"; // green
    if (66 >= score && score >= 34) return "#f97316"; // orange
    return "#ef4444"; // red
  };

  useEffect(() => {
    setCalls(callsFromStore);
  }, [callsFromStore]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(event.target as Node)
      ) {
        setIsSortOpen(false);
      }
    };

    if (isSortOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isSortOpen]);

  const toggleSectorExpansion = (callId: string) => {
    setExpandedSectors((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(callId)) {
        newSet.delete(callId);
      } else {
        newSet.add(callId);
      }
      return newSet;
    });
  };

  const handleBookmarkAction = async (
    callId: string,
    isBookmarked: boolean,
  ) => {
    // Optimistic update
    setCalls((currentCalls) =>
      currentCalls.map((call) =>
        call.id === callId
          ? { ...call, is_call_bookmarked: !isBookmarked }
          : call,
      ),
    );

    try {
      await apiService.post(API_ENDPOINTS.CALLS.ACTION, {
        call_id: parseInt(callId),
        action_mode: isBookmarked ? "remove" : "add",
        action_type: "bookmark",
      });
      toast.showSuccess(isBookmarked ? "Bookmark removed" : "Call bookmarked");
      fetchMatchingCallsWithFilters(
        {
          sort: sortBy,
          search: searchQuery || undefined,
        },
        true,
      );
    } catch (error) {
      // Revert on error
      setCalls((currentCalls) =>
        currentCalls.map((call) =>
          call.id === callId
            ? { ...call, is_call_bookmarked: isBookmarked }
            : call,
        ),
      );
      console.error("Failed to update bookmark:", error);
      toast.showError("Failed to update bookmark");
    }
  };

  const sortOptions = {
    score_desc: "Best Match",
    created_desc: "Recently added",
    deadline_asc: "Upcoming deadlines",
  };

  const handleSortChange = (newSort: SortOption) => {
    setSortInStore(newSort);
    setIsSortOpen(false);
    fetchMatchingCallsWithFilters({
      sort: newSort,
      search: searchQuery || undefined,
    });
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      // Refetch in background to keep store in sync
      fetchMatchingCallsWithFilters(
        {
          sort: sortBy,
          search: searchQuery || undefined,
        },
        true,
      );
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleApplyFilters = (appliedFilters: FilterState) => {
    const newFilters: MatchingCallsFilters = {
      ...filters,
      sort: sortBy,
      programmes: appliedFilters.programmes.join(","),
      deadline_from_date: appliedFilters.fromDate
        ? appliedFilters.fromDate.toISOString().split("T")[0]
        : undefined,
      deadline_to_date: appliedFilters.toDate
        ? appliedFilters.toDate.toISOString().split("T")[0]
        : undefined,
    };
    setFilters(newFilters);
    fetchMatchingCallsWithFilters(newFilters);
    setIsFiltersDrawerOpen(false);
  };

  // ✅ SIMPLIFIED: Just open modal, no API call
  const handleEdit = () => {
    if (goal) {
      // console.log("🔵 Opening edit modal for goal:", goal.id);
      setIsEditModalOpen(true);
      setIsMenuOpen(false);

      // If you need entity data, uncomment this:
      getEntityCall({ goalId: goal.id });
    }
  };

  const handleDelete = async () => {
    if (goal) {
      try {
        await goalsService.deleteGoal(goal.id);
        fetchGoals(undefined);
        onBack();
      } catch (error) {
        console.error("Failed to delete goal", error);
        toast.showError("Failed to delete goal");
      }
    }
  };

  const handleModalHide = () => {
    setIsEditModalOpen(false);
    if (goal) {
      fetchGoal({ goalId: goal.id });
    }
  };

  const handleExportCall = async () => {
    setIsExporting(true);
    if (goal) {
      try {
        const exportBlob = await callsService.exportGoalCalls(goal.id);

        const url = window.URL.createObjectURL(exportBlob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `goal-${goal.id}-calls-export.pdf`;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.showSuccess("Call exported successfully");
      } catch (error) {
        console.error("Export error:", error);
        toast.showError("Failed to export call");
      } finally {
        setIsExporting(false);
      }
    }
  };

  const handleLoadMore = () => {
    loadMoreCalls(filters);
  };

  const displayedCalls = calls;

  if (loading || !goal) {
    return <Loader overlay />;
  }

  return (
    <div className="matching-calls">
      <CreateGoalModal
        visible={isEditModalOpen}
        onHide={handleModalHide}
        goal={goal}
        entity={entity as FundingGoalEntityType}
      />
      <div className="matching-calls__summary">
        <div className="summary-header">
          {goal.analyzed === "1" ? (
            <div className="summary-title">
              <h2>{goal.matching_calls || 0} matching calls</h2>
              <Tag
                value={`up to ${goal.max_score || 0}% match`}
                className="match-badge"
                style={{
                  backgroundColor: getScoreColor(Number(goal.max_score)),
                }}
              />
            </div>
          ) : (
            <div className="summary-title">
              <h2>Pending...</h2>
            </div>
          )}
          <div className="summary-actions">
            <button
              className="kebab-btn"
              onClick={() => setIsMenuOpen((v) => !v)}
              aria-label="Open menu"
            >
              <i className="pi pi-ellipsis-v" />
            </button>
            {isMenuOpen && (
              <div className="summary-dropdown">
                <button className="dropdown-item" onClick={handleEdit}>
                  <i className="pi pi-cog" /> Edit goal
                </button>
                <button className="dropdown-item danger" onClick={handleDelete}>
                  <i className="pi pi-trash" /> Delete goal
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="summary-chips">
          {goal.expenditure && (
            <>
              <span className="meta-label">Category</span>
              <Tag value={goal.expenditure.name} className="chip" />
            </>
          )}
          {goal.range && (
            <>
              <span className="meta-label">Budget</span>
              <Tag value={goal.range.name} className="chip" />
            </>
          )}
          {goal.types?.length ? <span className="meta-label">Type</span> : null}
          {goal.types.map((type) => (
            <Tag key={type.id} value={type.name} className="chip" />
          ))}
        </div>
        {goal.sectors?.length ? (
          <div className="summary-sectors">
            {goal.sectors.slice(0, 5).map((s) => (
              <Tag key={s.id} value={s.name} className="chip" />
            ))}
          </div>
        ) : null}
      </div>

      <div className="matching-calls__header">
        <Button icon="pi pi-arrow-left" label="Back" text onClick={onBack} />
        <h2>Results</h2>
      </div>

      <div className="calls-page calls-page--embedded">
        <div className="call-filters">
          <div className="filter-search">
            <input
              type="text"
              placeholder="Search"
              className="search-input"
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchSubmit}
            />
          </div>
          <div className="filter-controls">
            <button
              className={`filter-btn list-view-btn ${
                viewMode === "list" ? "active" : ""
              }`}
              onClick={() => setViewMode(viewMode === "grid" ? "list" : "grid")}
            >
              <SvgIcon
                icon={viewMode === "grid" ? "icon-list" : "icon-grid-view"}
              />
              {viewMode === "grid" ? "List view" : "Grid view"}
            </button>
            <div className="sort-dropdown" ref={sortDropdownRef}>
              <button
                className="filter-btn sort-btn"
                onClick={() => setIsSortOpen(!isSortOpen)}
              >
                <SvgIcon icon="icon-sort" />{" "}
                {sortOptions[sortBy as keyof typeof sortOptions] ||
                  "Score descending"}
                <SvgIcon
                  icon={isSortOpen ? "icon-chevron-up" : "icon-chevron-down"}
                />
              </button>
              {isSortOpen && (
                <div className="sort-dropdown-menu">
                  {Object.entries(sortOptions).map(([key, label]) => (
                    <button
                      key={key}
                      className={`sort-option ${
                        sortBy === key ? "active" : ""
                      }`}
                      onClick={() => handleSortChange(key as SortOption)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              className="filter-btn filters-btn"
              onClick={() => setIsFiltersDrawerOpen(true)}
            >
              <SvgIcon icon="icon-filter" /> Filters
            </button>
            <Button
              className="exportButton"
              onClick={() => {
                handleExportCall();
              }}
              disabled={isExporting}
            >
              <SvgIcon icon="icon-export" />
              <div>{isExporting ? "Exporting call" : "Export calls"}</div>
              <Tags type="pro" />
              <Tooltip
                text="Export all for-you calls as PDF document"
                position="top"
              >
                <SvgIcon icon="icon-help" />
              </Tooltip>
            </Button>
            <FiltersDrawer
              isOpen={isFiltersDrawerOpen}
              onClose={() => setIsFiltersDrawerOpen(false)}
              onApply={handleApplyFilters}
              matchingCalls={true}
            />
          </div>
        </div>

        {goal.analyzed === "1" ? (
          <div>
            <div className="calls-results">
              {goalsState.calls_count} results
            </div>

            <div className="calls-content">
              {displayedCalls.length > 0 ? (
                <div
                  className={`calls-list ${
                    viewMode === "list" ? "list-view" : "grid-view"
                  }`}
                >
                  {displayedCalls.map((call) => (
                    <div
                      key={`${slugify(call.title)}-${call.id}`}
                      className="call-item eu-card"
                    >
                      <Link
                        to={createRoute(ROUTES.CALL_DETAIL, {
                          id: call.id,
                        })}
                      >
                        <div className="call-header">
                          <div className="call-type">
                            <div className="eu-flag">
                              <img src={call.programme.logo} alt="EU call" />
                            </div>
                            <span>{call.programme.name}</span>
                          </div>
                          <button
                            className={`bookmark-btn ${
                              call.is_call_bookmarked ? "bookmarked" : ""
                            }`}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleBookmarkAction(
                                call.id,
                                call.is_call_bookmarked,
                              );
                            }}
                          >
                            <SvgIcon
                              icon={`${
                                call.is_call_bookmarked
                                  ? "icon-bookmarked"
                                  : "icon-bookmark"
                              }`}
                            />
                          </button>
                        </div>
                        {viewMode === "list" && (
                          <div className="call-footer">
                            <div
                              className="match-badge"
                              style={{
                                backgroundColor: getScoreColor(
                                  Number(call.score),
                                ),
                              }}
                            >
                              {call.score}% match
                            </div>
                          </div>
                        )}

                        <div className="call-content">
                          <div className="call-badges">
                            {call.is_new && (
                              <span className="badge new-badge">New</span>
                            )}
                            <span className="badge deadline-badge">
                              <SvgIcon icon="icon-clock" /> until{" "}
                              {new Date(call.deadline).toLocaleDateString(
                                "en-GB",
                                {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                },
                              )}
                            </span>
                          </div>

                          <h3 className="call-title">{call.title}</h3>

                          <div className="call-budget">
                            €{" "}
                            {call.budget
                              ? parseInt(call.budget).toLocaleString()
                              : "0"}
                          </div>

                          {viewMode === "list" ? (
                            <div className="call-meta">
                              <div className="interested-partners">
                                <span className="partners-label">
                                  Interested Partners
                                </span>
                                <div className="partners-logos">
                                  {call.interested_partners &&
                                    call.interested_partners.length > 0 && (
                                      <span className="partners-count">
                                        +{call.interested_partners.length}
                                      </span>
                                    )}
                                </div>
                              </div>
                              <div className="call-stats">
                                <div className="stat-item item-comments">
                                  <SvgIcon icon="icon-message" />{" "}
                                  {call.comments_count} comments
                                </div>
                                <div className="stat-item item-ideas">
                                  <SvgIcon icon="icon-ideas" />{" "}
                                  {call.ideas_count} ideas
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="interested-partners">
                              <span className="partners-label">
                                Interested Partners
                              </span>
                              <div className="partners-logos">
                                {call.interested_partners &&
                                  call.interested_partners.length > 0 && (
                                    <span className="partners-count">
                                      +{call.interested_partners.length}
                                    </span>
                                  )}
                              </div>
                            </div>
                          )}

                          <div className="sectors-tags">
                            {expandedSectors.has(call.id) ? (
                              <>
                                {call.sectors.map((sector) => (
                                  <span
                                    key={`${call.id}-${slugify(sector.name)}-${
                                      sector.id
                                    }`}
                                    className="sector-tag"
                                  >
                                    {sector.name}
                                  </span>
                                ))}
                                <span
                                  className="sector-tag more-count"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    toggleSectorExpansion(call.id);
                                  }}
                                >
                                  less
                                </span>
                              </>
                            ) : (
                              <>
                                {call.sectors.slice(0, 4).map((sector) => (
                                  <span
                                    key={`${call.id}-${slugify(sector.name)}-${
                                      sector.id
                                    }`}
                                    className="sector-tag"
                                  >
                                    {sector.name}
                                  </span>
                                ))}
                                {call.sectors.length > 4 && (
                                  <span
                                    className="sector-tag more-count"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      toggleSectorExpansion(call.id);
                                    }}
                                  >
                                    +{call.sectors.length - 4}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        {viewMode === "grid" && (
                          <div className="call-footer">
                            <div
                              className="match-badge"
                              style={{
                                backgroundColor: getScoreColor(
                                  Number(call.score),
                                ),
                              }}
                            >
                              {call.score}% match
                            </div>
                            <div className="call-stats">
                              <div className="stat-item item-comments">
                                <SvgIcon icon="icon-message" />{" "}
                                {call.comments_count} comments
                              </div>
                              <div className="stat-item item-ideas">
                                <SvgIcon icon="icon-ideas" /> {call.ideas_count}{" "}
                                ideas
                              </div>
                            </div>
                          </div>
                        )}
                      </Link>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="nocalls-message">
                  <div className="nocalls-message-container">
                    <img src={imageList.targetLogo} alt="target-logo" />
                    <div className="nocalls-message-title">
                      No calls that match your criteria yet
                    </div>
                    <div className="nocalls-message-text">
                      Adjust your goal to find the right matching now or wait
                      until our database is updated with new calls
                    </div>
                  </div>
                </div>
              )}
            </div>

            {hasMoreCalls() && (
              <div className="calls-action">
                {loadMore ? (
                  <Loader message="Loading" />
                ) : (
                  <button
                    ref={loadMoreButtonRef}
                    className="calls-loadMore"
                    onClick={handleLoadMore}
                    disabled={loadMore}
                  >
                    Load more
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="calls-action">
            <button
              className="calls-loadMore"
              onClick={() => analyzeCall({ goalId: goal.id })}
            >
              Analyze
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
